import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Tone } from './StatusBadge';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
}

const subscribers = new Set<(t: ToastItem) => void>();
let counter = 0;

export function toast(message: string, tone: Tone = 'ok'): void {
  counter += 1;
  const item: ToastItem = { id: counter, message, tone };
  subscribers.forEach((cb) => cb(item));
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);

  const handleNew = useCallback((t: ToastItem) => {
    setItems((prev) => [...prev, t]);
    setTimeout(() => setItems((prev) => prev.filter((p) => p.id !== t.id)), 3500);
  }, []);

  useEffect(() => {
    subscribers.add(handleNew);
    return () => { subscribers.delete(handleNew); };
  }, [handleNew]);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        insetInlineEnd: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 2000,
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          role="status"
          style={{
            padding: 'var(--pad-row) var(--pad-card)',
            background: `var(--${item.tone}-soft, var(--surface))`,
            color: `var(--${item.tone}, var(--text))`,
            border: `1px solid var(--${item.tone}, var(--line))`,
            borderRadius: 'var(--r-2)',
            boxShadow: 'var(--shadow-2)',
            fontSize: 'var(--t-sm)',
            pointerEvents: 'auto',
            animation: 'fadein 120ms ease',
          }}
        >
          {item.message}
        </div>
      ))}
    </div>,
    document.body
  );
}
